import * as go from 'gojs';
import * as React from 'react';

import {
  LinkCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { selectAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/selectAttribute';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectAttributesByIriSelector } from 'src/selectors/projectAttributesByIri';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useClickModelConcept = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const attributesByIri = useReduxSelector(
    projectAttributesByIriSelector(projectId)
  );

  return React.useCallback(
    (iri: string) => {
      if (!diagram) {
        return;
      }

      if (attributesByIri[iri]) {
        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) => selectAttribute(iri, true)(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );

        dispatch(
          uiSlice.actions.changedEntityDrawer({
            projectId,
            drawer: {
              entityValue: iri,
              type: EntityDrawerType.ATTRIBUTE,
            },
          })
        );
        return;
      }

      const nodes = diagram.findNodesByExample({
        value: iri,
        category: NodeCategory.MODEL_CONCEPT_CLASS,
      });
      if (nodes.count) {
        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) => setDiagramSelection(new go.Set(nodes), true)(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );

        const firstNode = nodes.first();
        if (firstNode) {
          dispatch(
            uiSlice.actions.changedEntityDrawer({
              projectId,
              drawer: {
                entityValue: firstNode.data.value,
                type: EntityDrawerType.CLASS,
              },
            })
          );
        }
        return;
      }

      const links = diagram.findLinksByExample({
        value: iri,
        category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
      });
      if (links.count) {
        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) => setDiagramSelection(new go.Set(links), true)(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );

        const firstLink = links.first();
        if (firstLink) {
          dispatch(
            uiSlice.actions.changedEntityDrawer({
              projectId,
              drawer: {
                entityValue: firstLink.data.value,
                type: EntityDrawerType.RELATIONSHIP,
              },
            })
          );
        }
      }
    },
    [attributesByIri, diagram, dispatch, projectId]
  );
};
